/**
 * Single source of truth for Brand Equity outcome-metric identity.
 *
 * The modeling pipeline owns the raw outcome-metric identifier (the stage-one
 * `kpi`, e.g. `gqv`, `gqv_combined`). The frontend gives the
 * query-volume (GQV) family special display treatment. Historically that was
 * detected with exact string matches (`metric === "Gqv"`,
 * `metric.toUpperCase() === "GQV"`) scattered across the pages, so a new variant
 * like `gqv_combined` silently lost its GQV treatment — and, before the render
 * guards, crashed the page.
 *
 * Centralizing detection here means the pipeline can introduce new GQV variants
 * without a frontend change: any identifier in the gqv family is recognized. This
 * does NOT match the brand-health *channel* named `gqv` inside decomposition rows
 * — that channel was not renamed and its handling stays local.
 */

/**
 * True when the given outcome-metric identifier or label represents Google query
 * volume (GQV). Accepts any casing and either the raw `kpi` (`"gqv"`,
 * `"gqv_combined"`) or its formatted label (`"Gqv"`, `"Gqv_combined"`).
 */
export const isQueryVolumeMetric = (metric?: string | null): boolean => {
  if (!metric) {
    return false;
  }
  return metric.toLowerCase().startsWith('gqv');
};
